/**
 * Payment type labels — the one place a booking's `paymentType` becomes words.
 *
 * Mobile, web and the admin portal each used to capitalise the raw value, which gave a
 * member "Comp" on one app and "Complimentary" on the other, and an admin
 * "Membership_cross_club". Keyed on every `PAYMENT_TYPE`, so a new type is a compile error
 * here rather than a raw value on a screen.
 *
 * A plain paid booking is a "Standard Booking" — not "Direct", and not "Walk-in", which is
 * reserved for money taken at the counter.
 *
 * @module butterUI/domains/booking/paymentTypeLabel
 */

import { PAYMENT_TYPE, type PaymentType } from '@lgc/database/types/domains/booking'

const STANDARD_BOOKING = 'Standard Booking'

const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  [PAYMENT_TYPE.DIRECT]: STANDARD_BOOKING,
  [PAYMENT_TYPE.MEMBERSHIP]: 'Membership',
  [PAYMENT_TYPE.MEMBERSHIP_HYBRID]: 'Membership + Overage',
  [PAYMENT_TYPE.MEMBERSHIP_CROSS_CLUB]: 'Membership (other club)',
  [PAYMENT_TYPE.PASS]: 'Pass',
  [PAYMENT_TYPE.PASS_HYBRID]: 'Pass + Overage',
  [PAYMENT_TYPE.PASS_CROSS_CLUB]: 'Pass (other club)',
  [PAYMENT_TYPE.TEAM]: 'Team',
  [PAYMENT_TYPE.COMP]: 'Complimentary',
  [PAYMENT_TYPE.DEMO]: 'Demo',
  [PAYMENT_TYPE.LESSON]: 'Lesson',
  [PAYMENT_TYPE.LESSON_HYBRID]: 'Lesson',
  [PAYMENT_TYPE.LESSON_DIRECT]: 'Lesson',
}

/**
 * The label for a booking's payment type. Anything unrecognised — a missing value, or a
 * client-side value such as web's `walk_in` that never reaches the table — reads as a
 * Standard Booking rather than as a raw string.
 */
export function formatPaymentTypeLabel(paymentType: string | null | undefined): string {
  if (!paymentType) return STANDARD_BOOKING
  return (PAYMENT_TYPE_LABELS as Record<string, string>)[paymentType] ?? STANDARD_BOOKING
}
